/* eslint-disable no-inline-styles/no-inline-styles */
import type { ContentBoxSwiperDesktopProps } from '@/core/features/swiper/content-box-swiper-desktop/content-box-swiper-desktop-types';
import type { Swiper } from 'swiper/types';

import React from 'react';

import ChevronRightSmallIcon from '@/assets/icons/chevron-right-small';
import { InView, SwiperWrapper } from '@/core/features';
import useWindowResize from '@/core/features/event-listener/use-window-resize';
import color from '@/core/features/styles/color';
import { getSnappedSlidesPerView } from '@/core/features/swiper/content-box-swiper-desktop/content-box-swiper-desktop-service';

/*
 * This Component should be used within a container with 20px
 *
 * - id has to be unique on a given page and can't have spaces. Use e.g. the corresponding component id
 * - maxSlideWidth has to be rounded up
 * - minSlideWidth has to be rounded down
 * - slidesPerView is the target value which is tried to be achieved if maxSlideWidth and minSlideWidth is defined
 */
export default function ContentBoxSwiperDesktop({
    breakpoints,
    children,
    componentId,
    disableAutoSwiperSlideIndex = false,
    disableLazyLoading,
    disableSwiperZIndex = false,
    enabled = true,
    initialSlide,
    loadMore,
    maxSlideWidth,
    minSlideWidth,
    navigationButtonInset = 0,
    navigationButtonSize = 48,
    onResize,
    onSwiper,
    slidesPerView,
    snapToFullSlides,
    swiperModuleOverrides,
    swiperSlideProps,
    targetSlidesPerView,
}: ContentBoxSwiperDesktopProps) {
    const swiperPrev = `swiperPrev-${componentId}`;
    const swiperNext = `swiperNext-${componentId}`;
    const [currentSlidesPerView, setCurrentSlidesPerView] = React.useState(slidesPerView ?? targetSlidesPerView);
    const [slideElement, setSlideElement] = React.useState<Element>();
    const [swiperInstance, setSwiperInstance] = React.useState<Swiper | null>(null);
    const [style, setStyle] = React.useState<React.CSSProperties>({});

    const [shouldRenderLoadingTiles, setRenderLoadingTiles] = React.useState(false);

    const [preventNextOverflow, setPreventNextOverflow] = React.useState(false);
    const [rightThreshold, setRightThreshold] = React.useState<null | number>(null);
    const nextButtonRef = React.useRef<HTMLDivElement>(null);
    const [preventPrevOverflow, setPreventPrevOverflow] = React.useState(false);
    const [leftThreshold, setLeftThreshold] = React.useState<null | number>(null);
    const prevButtonRef = React.useRef<HTMLDivElement>(null);
    const { windowSize } = useWindowResize();

    const onSwiperWrapper = (swiper: Swiper) => {
        onSwiper?.(swiper);
        setSwiperInstance(swiper);
        setTimeout(() => {
            // Get the first (and typically only) element of the first slide which then represents all slideElements
            setSlideElement(swiper.slides?.[0]?.children?.[0]);
        }, 0);
    };

    const onResizeWrapper = React.useCallback(
        (swiper: Swiper) => {
            onResize?.(swiper);

            if (
                minSlideWidth === undefined ||
                maxSlideWidth === undefined ||
                !slideElement ||
                swiper.slides.length !== children?.length
            ) {
                return;
            }

            if (!targetSlidesPerView) {
                return;
            }

            const snappedSlidesPerView = getSnappedSlidesPerView({
                maxSlideWidth,
                minSlideWidth,
                slideWidth: slideElement.clientWidth,
                snapToFullSlides: snapToFullSlides ?? false,
                spaceBetweenSlides: Number(swiper.params.spaceBetween) ?? 0,
                swiperWidth: swiper.width,
                targetSlidesPerView,
            });
            setCurrentSlidesPerView(snappedSlidesPerView);
        },
        [children?.length, maxSlideWidth, minSlideWidth, onResize, slideElement, snapToFullSlides, targetSlidesPerView],
    );

    React.useEffect(() => {
        if (!nextButtonRef.current) {
            return;
        }

        const rect = nextButtonRef.current.getBoundingClientRect();
        const isOverflowingRight = rect.right > windowSize.innerWidth;

        if (isOverflowingRight && rightThreshold === null) {
            setPreventNextOverflow(true);
            setRightThreshold(rect.right);
        } else if (rightThreshold !== null && windowSize.innerWidth > rightThreshold) {
            setPreventNextOverflow(false);
            setRightThreshold(null);
        }
    }, [windowSize, rightThreshold]);

    React.useEffect(() => {
        if (!prevButtonRef.current) {
            return;
        }

        const rect = prevButtonRef.current.getBoundingClientRect();
        const isOverflowingLeft = rect.left < 0;

        if (isOverflowingLeft && leftThreshold === null) {
            setPreventPrevOverflow(true);
            setLeftThreshold(windowSize.innerWidth - rect.left);
        } else if (leftThreshold !== null && windowSize.innerWidth > leftThreshold) {
            setPreventPrevOverflow(false);
            setLeftThreshold(null);
        }
    }, [windowSize, leftThreshold]);

    React.useEffect(() => {
        if (swiperInstance) {
            onResizeWrapper(swiperInstance);
            if (typeof currentSlidesPerView === 'number' && swiperInstance?.slides?.length < currentSlidesPerView) {
                setStyle({ minWidth: maxSlideWidth });
            } else {
                setStyle({});
            }
        }
    }, [swiperInstance, onResizeWrapper, currentSlidesPerView, maxSlideWidth]);

    React.useEffect(() => {
        if (!loadMore) {
            return;
        }

        if (loadMore.hasLoadMore) {
            setRenderLoadingTiles(true);
            return;
        }

        if (!loadMore.isLoading) {
            setTimeout(() => {
                setRenderLoadingTiles(false);
            }, 0);
        }
    }, [loadMore]);

    return (
        <div
            className={'flex-center width-100 relative'}
            style={{ isolation: 'isolate' }}
        >
            <div className={'swiperControl'}>
                <div
                    className={`${swiperPrev} left-0 absolute border-radius-circle flex-center pointer user-select-none bg-white z-index-2`}
                    data-qa-id={'qa-swiper-control-left-desktop'}
                    ref={prevButtonRef}
                    style={{
                        boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.2)',
                        height: navigationButtonSize,
                        opacity: 0.9,
                        top: '50%',
                        transform: `translateX(${preventPrevOverflow ? '-20%' : '-50%'}) translateX(${navigationButtonInset}px) translateY(-50%)`,
                        width: navigationButtonSize,
                    }}
                >
                    <ChevronRightSmallIcon
                        color={color('nobel')}
                        height={16}
                        rotate={180}
                        width={9}
                    />
                </div>
            </div>

            <SwiperWrapper
                breakpoints={breakpoints}
                debugName={'contentBoxSwiperDesktop'}
                disableAutoSwiperSlideIndex={disableAutoSwiperSlideIndex}
                disableLazyLoading={disableLazyLoading}
                disableSwiperZIndex={disableSwiperZIndex}
                enabled={enabled}
                initialSlide={initialSlide}
                navigation={{ nextEl: `.${swiperNext}`, prevEl: `.${swiperPrev}` }}
                onResize={onResizeWrapper}
                onSwiper={onSwiperWrapper}
                slidesPerView={currentSlidesPerView}
                swiperModuleOverrides={swiperModuleOverrides}
                swiperModuleType={'contentBoxSwiperDesktop'}
                swiperSlideProps={{
                    ...swiperSlideProps,
                    style: { ...style, ...swiperSlideProps?.style },
                }}
            >
                {children}
                {loadMore?.loadingTile &&
                    shouldRenderLoadingTiles &&
                    Array.from({ length: Math.max(currentSlidesPerView, 5) }).map((_, index) => (
                        <InView
                            className={'width-100 height-100'}
                            key={index}
                            onChange={(inView) => {
                                return inView && !loadMore.isLoading ? loadMore.onLoadMore() : undefined;
                            }}
                        >
                            <loadMore.loadingTile />
                        </InView>
                    ))}
            </SwiperWrapper>
            {/* keep showing next button until there are no more pages to load */}
            <div className={loadMore?.hasLoadMore ? '' : 'swiperControl'}>
                <div
                    className={`${swiperNext} right-0 absolute border-radius-circle flex justify-center bg-white pointer user-select-none z-index-2`}
                    data-qa-id={'qa-swiper-control-right-desktop'}
                    ref={nextButtonRef}
                    style={{
                        boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.2)',
                        height: navigationButtonSize,
                        opacity: 0.9,
                        top: '50%',
                        transform: `translateX(${preventNextOverflow ? '20%' : '50%'}) translateX(${-navigationButtonInset}px) translateY(-50%)`,
                        width: navigationButtonSize,
                    }}
                >
                    <ChevronRightSmallIcon
                        color={color('nobel')}
                        height={16}
                        width={9}
                    />
                </div>
            </div>
            <style jsx>{`
                .swiperControl {
                    :global(.swiper-button-disabled) {
                        opacity: 0 !important;
                        pointer-events: none;
                    }
                }

                .${swiperNext}:hover, .${swiperPrev}:hover {
                    background-color: ${color('wildSand')};
                }

                .fade {
                    display: none;
                    pointer-events: none;
                }

                @media (max-width: 1290px) {
                    .fade {
                        display: block;
                        position: absolute;
                        top: 0;
                        z-index: 1;
                        height: 100%;
                        width: 400px;
                        background: linear-gradient(
                            270deg,
                            ${color('white')} 0%,
                            ${color('white-08')} 50%,
                            ${color('white-00')} 100%
                        );
                    }
                }
            `}</style>
        </div>
    );
}
